/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@ant-design/icons';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import { DefaultVariantId, HubServiceState } from '@kemu-io/kemu-core/types';
import { SerializableServiceInfo } from '@kemu-io/hs-types';
import { useDebouncedCallback } from 'use-debounce';
import { findRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { RecipeType } from '@kemu-io/kemu-types';
import { GetPortsInformationFunction, GateUI, GateUIProps } from '../index';
import { calculateWidgetColors } from '../common';
import styles from './hubService.module.css';
import HsUIWrapper from './HsUIWrapper/HsUIWrapper';
import { ReactComponent as DefaultServiceIcon } from '@src/assets/img/gates/hubService.svg';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import { PortLocation } from '@src/types/canvas_t';
import InjectSvg from '@components/InjectSvg/InjectSvg';
import useServiceUIContents from '@hooks/useServiceUIContents';
import useCompatibleHubService from '@hooks/useCompatibleHubService';
import { useHubContext } from '@common/context/HubContext/HubContext';
import { calculateStrChecksum } from '@common/utils/encryption';

const DefaultWidgetHeight = 64;

const docStyle = getComputedStyle(document.body);
const DefaultServiceColor =  docStyle.getPropertyValue('--kemu-color-widget-type-custom');
// const cache = emotionCache({key: 'widget-hub-service-a'});

const calculateWidgetHeight = (inputs: number | undefined, outputs: number | undefined): number => {
	// 10 = extra pixels per added port
	const portsHeight = ((Math.max(inputs || 1, outputs || 1)) * 17);
	return portsHeight;
};

const TooltipInfo = (props: {
	name: string,
	version: string,
	service?: SerializableServiceInfo | null,
	variantId?: string,
}): React.JSX.Element => {

	const { service, name, version, variantId } = props;
	const matchingVariant = service?.variants?.find((v) => v.id === variantId);
	const description = matchingVariant?.description || service?.description;
	return (
		<div className={styles.Tooltip}>
			<div className={styles.Header}>{name} (v{version})</div>
			{description && (
				<div className={styles.Description}>{description}</div>
			)}
		</div>
	);
};


const HubServiceWidget = (props: GateUIProps): React.JSX.Element => {
	const { recipeId, thingRecipeId, info, repaintExistingPorts, repaintLabels } = props;
	const { id: widgetId } = info;
	// const { getState } = useWidgetState<HubServiceState>(recipeId, thingRecipeId, widgetId);
	const [state, setWidgetState] = useReactiveWidgetState<HubServiceState>(recipeId, thingRecipeId, info.id/* , compareStates */);
	// const t = useTranslation('Widget.HubService');
	const { connector, status } = useHubContext();
	// const isAcknowledged = status === 'acknowledged';
	// const services = connector.getCachedServices();
	const serviceVersion = state.service?.version;
	const containerRef = useRef<HTMLDivElement>(null);
	const [manifestChecksum, setManifestChecksum] = useState<string | undefined>();
	const serviceName = state.service?.name;
	const { service: serviceManifest, initialized: compatibleServiceInitialized } = useCompatibleHubService(serviceName, serviceVersion);
	const widgetContents = useServiceUIContents(serviceName, serviceManifest?.version);
	const serviceOfflineRef = useRef(false);
	// const serviceManifest = useMemo(() => {
	// 	const matching = services.find((service) =>
	// 		service.name === serviceName
	// 		&& service.version === serviceVersion);

	// 	// if (matching) {
	// 	// 	lastFoundServiceManifest.current = matching;
	// 	// }
	// 	return matching;
	// }, [services, serviceName, serviceVersion]);

	// NOTE: We choose the manifest first in case the color changed
	const widgetColor = serviceManifest?.color || state.service?.color || DefaultServiceColor;
	const disabled = !!info.disabled;
	const serviceOffline = !serviceManifest;
	const hubOffline = status === 'disconnected';

	const colors = useMemo(() => {
    if (!widgetColor) { return undefined; }

    const docStyle = getComputedStyle(document.body);
    const disabledColor = docStyle.getPropertyValue('--kemu-color-disabled-widget');

    const colors = calculateWidgetColors(disabled || serviceOffline ? disabledColor : widgetColor);
    return colors;
  }, [widgetColor, disabled, serviceOffline]);

	const portsHeight = calculateWidgetHeight(state.service?.inputs?.length, state.service?.outputs?.length);


	const updateWidgetManifestInfo = useDebouncedCallback(async () => {
		if (!serviceManifest) { return; }

		const {
			uiContentChecksum,
			...contentLessManifest
		} = serviceManifest;
		const manifestStr = JSON.stringify(contentLessManifest);
		const checksum = await calculateStrChecksum(manifestStr);

		setManifestChecksum((old) => {
			setWidgetState((c) => {
				if (old !== checksum) {
					return {
						...c,
						service: contentLessManifest,
						$$$serviceId: serviceManifest.sessionId,
					};
				} else if (c.$$$serviceId !== serviceManifest.sessionId) {
					// Update the running session id
					return {
						...c,
						$$$serviceId: serviceManifest.sessionId,
					};
				}

				return c;
			});

			return checksum;
		});
	}, 100);

	// Keeps track of manifest file changes (inputs, color, name, etc)
	// and update the service state accordingly, this forces a new re-mounting
	// of the widget.
	useEffect(() => {
		if (serviceManifest) {
			updateWidgetManifestInfo();
		}

		return () => {
			updateWidgetManifestInfo.cancel();
		};
	}, [serviceManifest, updateWidgetManifestInfo]);

	/** 
	 * IMPORTANT: Here we re-initialize the service after it has come back online.
	 * This is mainly used for services in dev-mode that go offline often and would not get their
	 * initialize function called if they were already added to the workspace.
	 */
	useEffect(() => {
		if (!compatibleServiceInitialized) { return; }

		if (serviceOffline) {
			serviceOfflineRef.current = true;
		} else if (serviceOfflineRef.current) {
			const recipeInfo = findRecipe(recipeId);
			if (!recipeInfo) { return; }
			if(!state.$$$serviceId) { return; }

			serviceOfflineRef.current = false;
			connector.initializeServiceInstance({
				recipePoolId: recipeId,
				widgetId: widgetId,
				recipeName: recipeInfo.name,
				recipeType: RecipeType.Browser,
				variantId: state.variantId,
				sessionId: state.$$$serviceId,
			}).catch((e) => {
				// Allow unimplemented init functions to be ignored, this mirrors the same error handling in the hub service processor
				if (e?.errCode !== 'FNC_NOT_FOUND' /* RemoveInvokeError.FunctionNotFound */) {
					// Backwards compatibility with older versions of remoteInvoker without custom errors
					const functionNotDefined = !!(typeof e === 'string' && e.match('^Function ".*" not found.$'));
					if (!functionNotDefined) {
						throw e;
					} else {
						console.warn(`Widget [${widgetId}] does not have an init function.`);
					}
				}
			});
		}
	}, [serviceOffline, connector, recipeId, widgetId, state.$$$serviceId, state.variantId, compatibleServiceInitialized]);

	const handleRepaintPorts = useCallback(() => {
		repaintExistingPorts();
		repaintLabels();
	}, [repaintExistingPorts, repaintLabels]);

	// Custom UI
	if (widgetContents && state.service) {
		return (
			<HsUIWrapper
				hidden={!!props.hidden}
				recipeId={recipeId}
				repaintPorts={handleRepaintPorts}
				serviceSessionId={state.$$$serviceId}
				manifest={state.service}
				uiContent={widgetContents}
				widgetId={widgetId}
				contentsChecksum={serviceManifest?.uiContentChecksum}
				manifestChecksum={manifestChecksum}
				disabled={disabled}
				finalColor={widgetColor}
				serviceOnline={!!serviceManifest}
			/>
		);
	}

	const matchingVariant = state.service?.variants?.find((v) => v.id === state.variantId);
	const displayName = matchingVariant?.name
		|| state.service?.defaultVariantInfo?.shortTitle
		|| state.service?.defaultVariantInfo?.title
		|| state.service?.shortTitle
		|| state.service?.title
		|| state.service?.name as string;

	const serviceOrVariantIcon = matchingVariant?.svgIcon || state.service?.svgIcon;

	return (
		// k-hs-wrap is required so canvasGate.css run-mode highlight rules apply to hub service widgets
		<div className={classNames('k-hs-wrap', {
			[styles.Invisible]: props.hidden
		})}>
			<div
				style={{
					...(!disabled && !serviceOffline && !hubOffline && colors?.content ? {
						backgroundColor: colors.content,
					} : {}),
					borderColor: colors?.header,
					minHeight: `${DefaultWidgetHeight}px`,
					...(portsHeight > DefaultWidgetHeight ? {
						height: `${portsHeight}px`,
					} : {}),
				}}
			className={classNames(styles.GateBody, {
				[styles.WidgetDisabled]: disabled || serviceOffline || hubOffline,
			})}
			>
				{state.service?.shortTitle && serviceOffline && (
					<div
						style={{
							color: colors?.text,
						}}
						className={styles.BarTitle}
					>
						(offline)
					</div>
				)}

				{state.service && (
					<Tooltip
						mouseEnterDelay={1}
						title={
							<TooltipInfo
								name={displayName}
								version={state.service.version}
								service={serviceManifest || state.service}
								variantId={state.variantId}
							/>
						}
					>
						<div className={styles.Icon} ref={containerRef}>
							{serviceOrVariantIcon ? (
								<InjectSvg
									contents={serviceOrVariantIcon}
									width={32}
									height={32}
									targetElement={containerRef.current}
									fill={colors?.text}
								/>
							) : (
								<Icon component={DefaultServiceIcon} />
							)}
						</div>
					</Tooltip>
				)}

				<div className={classNames(styles.BottomCaption, {
					[styles.InsideGroup]: !!props.info.groupId,
				})}>
					{displayName}
				</div>
			</div>
		</div>
	);
};


const getPortsInformation: GetPortsInformationFunction<HubServiceState> = (state) => {
	// const portContext: WidgetPortContext = {
	// 	recipePoolId: widgetInfo.recipePoolId,
	// 	recipeType: widgetInfo.recipeType,
	// 	thingRecipeId: widgetInfo.thingRecipeId,
	// 	widgetId: widgetInfo.id,
	// };

	const portSize = 0.02;

	const getPositionFromIndex = (totalPorts: number, index: number, isInput: boolean): PortLocation => {
		const topPadding = totalPorts <= 12 ? 0.15 : 0.05;
		const inputFraction = ((1 - (topPadding + portSize)) / totalPorts);
		let space = (inputFraction * index) + topPadding;
		if (totalPorts === 1) { space = 0.5; }
		if (totalPorts === 2) { space = 0.30 + (0.4 * index); }
		if (totalPorts === 3) { space = 0.25 + (0.25 * index); }
		if (totalPorts === 4) { space = 0.22 + (0.19 * index); }
		if (totalPorts === 5) { space = 0.15 + (0.18 * index); }
		if (totalPorts === 6) { space = 0.12 + (0.15 * index); }
		if (totalPorts === 7) { space = 0.10 + (0.13 * index); }

		return [isInput ? 0 : 1, space, isInput ? -1 : 1, 0];
	};

	const variantId = state.variantId;
	const matchingVariant = state.service?.variants?.find((v) => v.id === state.variantId);

	const defaultInputs = variantId ?
		state.dynamicInputs[variantId] || matchingVariant?.inputs || []
		: state.dynamicInputs[DefaultVariantId] || state.service?.inputs || [];

	const defaultOutputs = variantId ?
		state.dynamicOutputs[variantId] || matchingVariant?.outputs || []
		: state.dynamicOutputs[DefaultVariantId] || state.service?.outputs || [];


	return {
		outputs: defaultOutputs.map((output, i) => {
			return  {
				name: output.name,
				type: output.type,
				triggerPort: output.triggerPort,
				position: getPositionFromIndex(defaultOutputs.length, i, false),
				...(output.jsonShape ? { jsonShape: output.jsonShape } : undefined),
				...(output.label ? { label: output.label } : undefined),
			};
		}),

		inputs: defaultInputs.map((input, i) => {
			return  {
				name: input.name,
				type: input.type,
				triggerPort: input.triggerPort,
				position: getPositionFromIndex(defaultInputs.length, i, true),
				...(input.jsonShape ? { jsonShape: input.jsonShape } : undefined),
				...(input.label ? { label: input.label } : undefined),
			};
		})
	};
};


export default {
	getPortsInformation,
	// BarIcon: GateBarIcon,
	Element: HubServiceWidget,
	getWrapperClass: () => styles.WidgetWrapper,
	// CustomSettingsDialog: GateCustomSettings,
	// hasTitle: true,
	// getWidgetTitle: (intl) => intl.formatMessage({ defaultMessage: 'Hub Service', id: 'Widget.HubService.DefaultTitle' }),
} as GateUI;
